import React, {
  useRef,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import parsePhoneNumberFromString from 'libphonenumber-js';
import api, {
  API_ERRORS,
  getApiErrorName,
  getApiErrorMessage,
} from '../../../services/api';
import getValidationErrors from '../../../utils/getValidationErrors';
import {
  LockIcon,
  MailIcon,
  UserCircleIcon,
  LinkedinIcon,
  GenderIcon,
  WhatsupIcon,
  PencilIcon2,
  UserIdCardIcon,
} from '../../../assets/svg/SVGComponents';
import { useModal } from '../../../hooks/modal';
import { useToast } from '../../../hooks/toast';
import { useAuth, IUser } from '../../../hooks/auth';
import { genderTexts, UserGender } from '../../../utils/enums';
import Input from '../../../components/Input';
import SimpleSelect from '../../../components/Selects/SimpleSelect';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import ResetPasswordModal from '../../../modals/ResetPasswordModal';
import {
  MainContainer,
  RedefinePassword,
  Footer,
  genderSelectStyles,
  schoolSelectStyles,
  SchoolHint,
  Title,
} from './styles';
import IRequestError from '../../../dtos/IRequestError';
import MaskedInput from '../../../components/MaskedInput';
import PrivacyPolicyModal from '../../../modals/PrivacyPolicyModal';
import CheckboxUnform from '../../../components/CheckboxUnform';
import AsyncCreateSelect from '../../../components/Selects/AsyncCreateSelect';
import CertificatesIcon from '../../../assets/svg/SVGComponents/CertificatesIcon';
import { useOnboarding } from '../../../hooks/onboarding';

interface ProfileFormData {
  name: string;
  email: string;
  phone?: string;
  linkedin?: string;
  gender: UserGender;
  school?: string;
  subjects?: string[];
  privacy_policy: boolean;
  cookies_policy: boolean;
}

const PersonalDataForm: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [creatingSchool, setCreatingSchool] = useState(false);
  const { t } = useTranslation();
  const { handleModal } = useModal();
  const { addToast } = useToast();
  const {
    user,
    updateUser,
    isSessionExpired,
    refreshCompletionStatus,
    onDefaultSchool,
  } = useAuth();
  const { markCurrentStepActionCompleted } = useOnboarding();

  const genderOptions = useMemo(() => {
    return Object.entries(genderTexts).map(keyValue => ({
      label: keyValue[1],
      value: keyValue[0],
    }));
  }, []);

  const numberMask = useMemo(() => {
    if (!user.phone) {
      return undefined;
    }
    const phoneNumber = parsePhoneNumberFromString(user.phone);

    if (phoneNumber && phoneNumber.isValid()) {
      return phoneNumber.formatNational();
    }

    return undefined;
  }, [user.phone]);

  const loadSchoolsOptions = useCallback(async (inputValue: string) => {
    const response = await api.get(
      `/schools?name=${inputValue}&limit=10&page=1`,
    );

    return response.data.map((institution: { id: string; name: string }) => ({
      label: institution.name,
      value: institution.id,
    }));
  }, []);

  const handleCreatableSelect = useCallback(async (inputValue: string) => {
    const response = await api.post('/subjects', {
      name: inputValue,
    });

    return response.data.map((subject: { id: string; name: string }) => ({
      label: subject.name,
      value: subject.id,
    }));
  }, []);

  const handleResetPasswordModal = useCallback(() => {
    handleModal(<ResetPasswordModal />);
  }, [handleModal]);

  const handlePolicyPrivacyModal = useCallback(() => {
    handleModal(<PrivacyPolicyModal />);
  }, [handleModal]);

  const handleSubmit = useCallback(
    async (data: ProfileFormData) => {
      formRef.current?.setErrors({});
      setLoading(true);

      try {
        const profileSchema = Yup.object().shape({
          name: Yup.string().required(t('yup_errors.name_required')),
          email: Yup.string()
            .required(t('yup_errors.email_required'))
            .email(t('yup_errors.invalid_email')),
          phone: Yup.string()
            .notRequired()
            .test('is-valid-phone', 'Número de telefone inválido', value => {
              if (!value) return true;

              const phoneNumber = parsePhoneNumberFromString(value, 'BR');
              return phoneNumber ? phoneNumber.isValid() : false;
            }),
          linkedin: Yup.string().url(),
          gender: Yup.string()
            .oneOf(Object.values(UserGender), t('yup_errors.gender_not_found'))
            .required(t('yup_errors.gender_required')),
          school: Yup.string().required(t('yup_errors.school_required')),
          privacy_policy: Yup.boolean()
            .oneOf([true], t('yup_errors.privacy_required'))
            .required(t('yup_errors.privacy_required')),
          cookies_policy: Yup.boolean().required(
            t('yup_errors.privacy_required'),
          ),
        });

        const validatedSchema = await profileSchema.validate(data, {
          abortEarly: false,
          stripUnknown: true,
        });

        const unmaskedPhone = data.phone?.replace(/\D/g, '');

        if (validatedSchema && unmaskedPhone) {
          const phoneNumber = parsePhoneNumberFromString(unmaskedPhone, 'BR');

          let formattedPhone = '';

          if (phoneNumber && phoneNumber.isValid()) {
            formattedPhone = phoneNumber.number; // Número no formato E.164
            validatedSchema.phone = formattedPhone;
          } else {
            addToast({
              type: 'error',
              expression: 'Ops!',
              description: t('toasts.errors.updated_data_fail'),
            });
          }
        }

        const response = await api.put<IUser>(`/user-profile`, validatedSchema);

        setLoading(false);
        updateUser(response.data);

        // A escola pode ter mudado aqui. Sem isto, o bloqueio de criar torneio
        // só sumiria no próximo login, com o usuário achando que não salvou.
        await refreshCompletionStatus();

        addToast({
          type: 'success',
          expression: 'Ok!',
          description: t('toasts.success.changes_saved'),
        });
        setIsSuccess(true);
      } catch (err: IRequestError | unknown) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
        } else if (
          getApiErrorName(err) === API_ERRORS.DEFAULT_SCHOOL_NOT_ALLOWED
        ) {
          formRef.current?.setFieldError('school', getApiErrorMessage(err));
          addToast({
            type: 'error',
            expression: 'Ops!',
            description: getApiErrorMessage(err),
          });
        } else if (!(await isSessionExpired(err as IRequestError))) {
          addToast({
            type: 'error',
            expression: 'Ops!',
            description: t('toasts.errors.updated_data_fail'),
          });
        }
      }
    },
    [t, updateUser, addToast, isSessionExpired, refreshCompletionStatus],
  );

  useEffect(() => {
    if (isSuccess) {
      markCurrentStepActionCompleted('update-info-profile');
    }
  }, [isSuccess, markCurrentStepActionCompleted]);

  /**
   * Cadastra a escola e JÁ a seleciona no campo. O `onCreateOption` do
   * react-select ignora o retorno da função, então sem o `setFieldValue` a
   * escola nascia no banco e o campo continuava vazio — de fora, "criar escola
   * não funciona".
   */
  const handleCreateSchool = useCallback(
    async (inputValue: string) => {
      setCreatingSchool(true);

      try {
        const response = await api.post('/schools', { name: inputValue });

        formRef.current?.setFieldValue('school', {
          label: response.data.name,
          value: response.data.id,
        });
        formRef.current?.setFieldError('school', '');
      } catch (err) {
        addToast({
          type: 'error',
          expression: 'Ops!',
          description:
            (err as IRequestError)?.response?.data?.message ??
            t('toasts.errors.school_create_fail', {
              defaultValue:
                'Não foi possível cadastrar a escola. Tente novamente.',
            }),
        });
      } finally {
        setCreatingSchool(false);
      }
    },
    [addToast, t],
  );

  const handleOnCreateSubjects = useCallback(async (inputValue: string) => {
    const response = await api.post('/subjects', {
      name: inputValue,
    });

    return response.data.map((subject: { id: string; name: string }) => ({
      label: subject.name,
      value: subject.id,
    }));
  }, []);

  return (
    <MainContainer className="personal-data-form">
      <Form
        ref={formRef}
        onSubmit={handleSubmit}
        initialData={{
          name: user.name,
          email: user.email,
          phone: numberMask,
          password: '******',
          linkedin: user.linkedin,
          gender: genderOptions.find(
            genderOption => genderOption.value === user.gender,
          ),
          school: user.school
            ? {
                value: user.school.id, // Assumindo que exista um ID
                label: user.school.name,
              }
            : null,
          privacy_policy: user.privacy_policy,
          cookies_policy: user.cookies_policy,
        }}
      >
        <header>
          <Title>
            <UserIdCardIcon
              width="1.7rem"
              stroke="#323232"
              strokeWidth={1.5}
              fill="none"
            />
            <h1>Dados Pessoais</h1>
          </Title>
          <PrimaryButton type="submit" loading={loading}>
            {t('buttons.save_changes')}
          </PrimaryButton>
        </header>

        <h4>{t('labels.teacher_name')}</h4>
        <Input
          name="name"
          type="text"
          placeholder={t('placeholders.full_name')}
          icon={UserCircleIcon}
        />

        <h4>{t('labels.phone')}</h4>
        <MaskedInput
          name="phone"
          placeholder={t('placeholders.phone')}
          icon={WhatsupIcon}
          mask="(99) 99999-9999"
          value={undefined}
        />

        <h4>{t('labels.email')}</h4>
        <Input
          name="email"
          type="text"
          placeholder={t('placeholders.email')}
          icon={MailIcon}
          readOnly
          disabled
          autoComplete="email"
        />

        <RedefinePassword>
          <h4>{t('labels.password')}</h4>
          <button type="button" onClick={handleResetPasswordModal}>
            {t('buttons.reset_password')}
          </button>
        </RedefinePassword>
        <Input
          name="password"
          placeholder={t('placeholders.password')}
          type="password"
          icon={LockIcon}
          readOnly
          disabled
          autoComplete="current-password"
        />

        <h4>{t('labels.linkedin')}</h4>
        <Input
          name="linkedin"
          type="text"
          placeholder="https://linkedin.com/in/example/"
          icon={LinkedinIcon}
        />

        <h4>{t('labels.gender')}</h4>
        <SimpleSelect
          name="gender"
          placeholder={t('placeholders.gender')}
          options={genderOptions}
          styles={genderSelectStyles}
          iconLeft={GenderIcon}
        />

        <h4>Instituição</h4>
        <AsyncCreateSelect
          name="school"
          className="schools"
          iconLeft={PencilIcon2}
          isLoading={creatingSchool}
          isDisabled={creatingSchool}
          placeholder={t('placeholders.institution')}
          formatCreateLabel={inputValue => `Cadastrar escola "${inputValue}"`}
          onCreateOption={handleCreateSchool}
          createOptionThreshold={3} // Só permite criar com pelo menos 3 caracteres
          noOptionsMessage={({ inputValue }) =>
            !inputValue
              ? 'Digite para buscar escolas'
              : 'Nenhuma escola encontrada'
          }
          isClearable
          styles={schoolSelectStyles}
          loadOptions={async (inputValue: string) => {
            const response = await loadSchoolsOptions(inputValue);
            return response;
          }}
        />

        {onDefaultSchool && (
          <SchoolHint>
            {t('profile.default_school_hint', {
              defaultValue:
                'Você está na escola padrão da plataforma (Fuzzy Makers). Escolha aqui o colégio ao qual você pertence — ou cadastre-o digitando o nome — para liberar a criação de torneios.',
            })}
          </SchoolHint>
        )}

        <CheckboxUnform name="privacy_policy" isBoolean>
          {t('navlinks.accept_policies_and_terms')}{' '}
          <button
            type="button"
            onClick={() => handlePolicyPrivacyModal()}
            style={{
              cursor: 'pointer',
              color: '#1E88E5',
            }}
          >
            {t('navlinks.policies_and_terms')}
          </button>
        </CheckboxUnform>

        <CheckboxUnform name="cookies_policy" isBoolean>
          {t('navlinks.cookies_and_privacy')}
        </CheckboxUnform>

        <Footer>
          <PrimaryButton
            type="submit"
            loading={loading}
            className="save-changes-button primary-button"
          >
            {t('buttons.save_changes')}
          </PrimaryButton>
        </Footer>
      </Form>
    </MainContainer>
  );
};

export default PersonalDataForm;
